/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { useMemo } from 'react';
import {
  type CreateLivefireCardCallbacks,
  type LivefireStatus,
  type LivefireStatusType,
  type OrganismState,
  type StatusActions,
  ViewState,
  errors,
  status,
  LivefireProps,
} from '../types';
import { DisplayError } from './DisplayError';
import { WrappedLoadingDots } from './WrappedLoadingDots';
import { Feed } from './Feed';
import { FeedItem } from '@/classes';
import { NonUndefined } from '@/functions/typeHelpers';
import { ImageEmptyState } from '@/layouts';
import { ErrorScreen } from '@/molecules';

export type MainComponentProps = {
  organismState: OrganismState;
  livefireStatus: LivefireStatus;
  viewState: ViewState;
  feedItems: FeedItem[];
  callbacks: CreateLivefireCardCallbacks;
  menuConfig: NonUndefined<LivefireProps['menuConfig']>;
  dispatch: React.Dispatch<StatusActions>;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  isRebrand?: boolean;
};

export function MainComponent({
  organismState,
  livefireStatus,
  viewState,
  feedItems,
  callbacks,
  menuConfig,
  dispatch,
  setViewState,
  isRebrand = false,
}: MainComponentProps) {
  // figure out various state flags
  const hasErrors = ([status.ERROR, status.RETRY_ERROR] as LivefireStatusType[]).includes(
    organismState.status,
  );
  const isLoaded = organismState.status === status.LOADED;
  const isEnvironmentSetup = !!livefireStatus.authToken;
  const isEnvironmentSetupWithNoErrors = isEnvironmentSetup && !hasErrors;
  const hasFeedItems = feedItems.length > 0;

  const { onEmptyStateClick, onErrorClick } = callbacks;

  const ensuredMenuConfig = useMemo(() => menuConfig || {}, [menuConfig]);
  const VIST_SUBSCRIPTION_ID = 'c733fa3d-2e01-4fa0-94f6-a70577337ef6';
  const isRecordButtonShown = livefireStatus.subscriptionId !== VIST_SUBSCRIPTION_ID;
  if (isEnvironmentSetupWithNoErrors) {
    if (!isLoaded) {
      return <WrappedLoadingDots />;
    }

    if (hasFeedItems) {
      return (
        <Feed
          organismState={organismState}
          callbacks={callbacks}
          menuConfig={ensuredMenuConfig}
          dispatch={dispatch}
          viewState={viewState}
          setViewState={setViewState}
          isRebrand={isRebrand}
        />
      );
    }

    // separated here because prettier line limits (and different from default
    // as forced line break does not look correct with new width)
    const emptyStateDescription =
      'Simply record and share your first video and tracking information will populate here.';
    return (
      <div className="empty-state-container">
        <ImageEmptyState
          onButtonClick={onEmptyStateClick}
          description={emptyStateDescription}
          showAction={isRecordButtonShown}
          isRebrand={isRebrand}
        />
      </div>
    );
  }

  if (hasErrors && organismState.error !== errors.NOT_LOGGED_IN) {
    return (
      <DisplayError organismState={organismState} dispatch={dispatch} onErrorClick={onErrorClick} />
    );
  }

  // by default, show a login error to handle cases where we don't have environment or token yet
  return (
    <div className="error-container">
      <ErrorScreen body="You must login to view your feed" icon="close" title="Please login" />
    </div>
  );
}
